// Redux Auth module
// see https://github.com/erikras/ducks-modular-redux

import { Auth, ApiKeyCheckTransientError } from '../../services';
import { User } from '../../models';
import {
    readSpoofOriginalUser,
    SPOOF_ORIGINAL_USER_STORAGE_KEY,
} from '../../services/spoofSession';
import {
    identify_posthog_user,
    reset_posthog,
    track_event,
} from '../../services/posthogAnalytics';

const LOG_IN           = 'kronos/auth/LOG_IN';
const LOG_IN_SUCCESS   = 'kronos/auth/LOG_IN_SUCCESS';
const LOG_IN_FAILURE   = 'kronos/auth/LOG_IN_FAILURE';
const REGISTER         = 'kronos/auth/REGISTER';
const REGISTER_SUCCESS = 'kronos/auth/REGISTER_SUCCESS';
const REGISTER_FAILURE = 'kronos/auth/REGISTER_FAILURE';
const SPOOF            = 'kronos/auth/SPOOF';
const SPOOF_SUCCESS    = 'kronos/auth/SPOOF_SUCCESS';
const SPOOF_FAILURE    = 'kronos/auth/SPOOF_FAILURE';
const UPDATE_AUTH_USER = 'kronos/auth/UPDATE_AUTH_USER';
const LOG_OUT          = 'kronos/auth/LOG_OUT';

function read_stored_user() {
    if (typeof localStorage === 'undefined') {
        return {};
    }
    try {
        const raw = localStorage.getItem('user');
        if (!raw || raw === 'null') {
            return {};
        }
        const user = JSON.parse(raw);
        if (user && typeof user === 'object' && 'id' in user) {
            return new User(user);
        }
    } catch (e) {
        /* ignore invalid stored session */
    }
    return {};
}

const defaultAuthState = {
    user: {},

    isAuthenticating: false,
    logInError: false,
    logInErrorMessage: '',

    isRegistering: false,
    registerError: false,
    registerErrorMessage: ''
};

const initialState = {
    ...defaultAuthState,
    user: read_stored_user()
};

// Reducer
export default function reducer(state = initialState, action) {
    switch (action.type) {
        case LOG_IN:
            return {
                ...state,
                isAuthenticating: true,
                logInError: false
            };
        case LOG_IN_SUCCESS:
            return {
                isAuthenticating: false,
                user: action.user
            };
        case LOG_IN_FAILURE:
            return {
                ...state,
                isAuthenticating: false,
                logInError: true,
                logInErrorMessage: action.error.message
            };
        case REGISTER:
            return {
                ...state,
                isRegistering: true,
                registerError: false
            };
        case REGISTER_SUCCESS:
            return {
                isRegistering: false,
                user: action.user
            };
        case REGISTER_FAILURE:
            return {
                ...state,
                isRegistering: false,
                registerError: true,
                registerErrorMessage: action.error.message
            };
        case SPOOF:
            return {
                ...state,
                isAuthenticating: true,
                logInError: false
            };
        case SPOOF_SUCCESS:
            return {
                isAuthenticating: false,
                user: action.user
            };
        case SPOOF_FAILURE:
            return {
                ...state,
                isAuthenticating: false,
                logInError: true,
                logInErrorMessage: action.error.message
            };
        case UPDATE_AUTH_USER:
            return {
                ...state,
                user: action.user
            };
        case LOG_OUT:
            return {
                ...defaultAuthState,
            };
        default:
            return state;
    }
};


// Actions
function logIn() { return { type: LOG_IN } }
function logInSuccess(user) { return { type: LOG_IN_SUCCESS, user: user } }
function logInFailure(err) { return { type: LOG_IN_FAILURE, error: err } }

export function getLastConnectedUser() {
    return (dispatch) => {
        const user = read_stored_user();
        if (user && user.api_key) {
            Auth.checkApiKey(user.api_key)
                .then((validated_user) => {
                    if (validated_user) {
                        dispatch(logInSuccess(validated_user));
                    } else {
                        dispatch(logOut());
                    }
                })
                .catch((err) => {
                    if (err instanceof ApiKeyCheckTransientError) {
                        return;
                    }
                    dispatch(logOut());
                });
        }
    }
};

export function authenticate(username, password) {
    return (dispatch) => {
        dispatch(logIn());
        Auth.logIn(username, password)
            .then(data => {
                const user = data.user;
                dispatch(logInSuccess(user));
                if (user?.id) {
                    identify_posthog_user(user.id);
                    track_event('kronos_login', { method: 'password' });
                }
            })
            .catch(err => {
                localStorage.setItem('user', null);
                dispatch(logInFailure(err))
            });
    }
}

function _register() { return { type: REGISTER } }
function _registerSuccess(user) { return { type: REGISTER_SUCCESS, user: user } }
function _registerFailure(err) { return { type: REGISTER_FAILURE, error: err } }
export function register(payload) {
    return (dispatch) => {
        dispatch(_register());
        Auth.register(payload)
            .then(data => {
                const user = data.user;
                dispatch(_registerSuccess(user));
            })
            .catch(err => {
                localStorage.setItem('user', null);
                dispatch(_registerFailure(err))
            });
    }
}

function _spoof() { return { type: SPOOF } }
function _spoofSuccess(user) { return { type: SPOOF_SUCCESS, user: user } }
function _spoofFailure(err) { return { type: SPOOF_FAILURE, error: err } }
export function spoof(id) {
    return (dispatch) => {
        dispatch(_spoof());
        return Auth.spoof(id)
            .then(data => {
                const user = data.user;
                dispatch(_spoofSuccess(user));
                // Drop privileged Redux caches so the UI matches the spoofed user.
                window.location.reload();
            })
            .catch(err => {
                dispatch(_spoofFailure(err));
            });
    }
}

function updateAuthUserSuccess(user) { return { type: UPDATE_AUTH_USER, user: user } }

export function updateAuthUser(user) {
    return (dispatch) => {
        if (!user?.id) {
            return;
        }
        let stored = null;
        try {
            const raw = localStorage.getItem('user');
            if (raw && raw !== 'null') {
                stored = JSON.parse(raw);
            }
        } catch (_e) {
            stored = null;
        }
        if (!stored?.id || String(stored.id) !== String(user.id)) {
            return;
        }
        const persisted = user instanceof User ? { ...user } : { ...user };
        if (!persisted.api_key && stored.api_key) {
            persisted.api_key = stored.api_key;
        }
        if (persisted.tickets) {
            delete persisted.tickets;
        }
        localStorage.setItem('user', JSON.stringify(persisted));
        const next_user = user instanceof User ? user : new User(persisted);
        if (!next_user.api_key && stored.api_key) {
            next_user.api_key = stored.api_key;
        }
        dispatch(updateAuthUserSuccess(next_user));
    };
}

function logout() { return { type: LOG_OUT } }

export function logOut() {
    return (dispatch) => {
        const original = readSpoofOriginalUser();
        if (original) {
            localStorage.removeItem(SPOOF_ORIGINAL_USER_STORAGE_KEY);
            localStorage.setItem('user', JSON.stringify(original));
            dispatch(logInSuccess(new User(original)));
            if (original?.id)
                identify_posthog_user(original.id);
            // Same as spoof: rebuild Redux so we do not keep the spoofed user's caches.
            window.location.reload();
            return;
        }
        track_event('kronos_logout');
        reset_posthog();
        localStorage.setItem('user', null);
        dispatch(logout());
    };
}
